import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import Toast from "./components/Toast";
import Header from "./components/Header";
import ModelsPage from "./pages/ModelsPage";
import ApiKeysPage from "./pages/ApiKeysPage";
import UsagePage from "./pages/UsagePage";
import SettingsPage from "./pages/SettingsPage";
import LandingPage from "./pages/LandingPage"; // Add this import
import DocumentationPage from "./pages/DocumentationPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import StatsSummary from "./components/StatsSummary";
import Logo from "./components/Logo";
import SupportPage from "./pages/SupportPage";
import CapacityReachedPage from "./pages/CapacityReachedPage";
import DashboardFooter from "./components/DashboardFooter";
import NotificationBar from "./components/NotificationBar";
import "./App.css";

const AdminApp = lazy(() => import("./admin/AdminApp"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [pathname]);
  return null;
}

function MainApp() {
  const { user, fetchingUser, toast, setToast, login } = useApp();

  if (fetchingUser) {
    return <div className="spinner" style={{ margin: '5rem auto' }} />;
  }

  if (!user) {
    return (
      <div className="login-container">
        <div className="login-card">
          {
            // <svg className="login-icon" fill="currentColor" viewBox="0 0 24 24">
            //   <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
            // </svg>
          }
          <Logo size={64} />
          <h1>OkeyBot Dashboard</h1>
          <p className="subtitle">Manage your AI provider API keys and models</p>
          <button className="btn btn-primary btn-full" onClick={login}>
            <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
            Login with Discord
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <NotificationBar />
      <Header />
      <div className="container">
        <StatsSummary />
        <Routes>
          <Route index element={<Navigate to="models" replace />} />
          <Route path="models" element={<ModelsPage />} />
          <Route path="keys" element={<ApiKeysPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="models" replace />} />
        </Routes>
        <DashboardFooter />
      </div>
    </>
  );
}

export default function App() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return <Suspense fallback={null}><AdminApp /></Suspense>;
  }

  return (
    <AppProvider>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/docs" element={<DocumentationPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/capacity" element={<CapacityReachedPage />} />
        <Route path="/dashboard/*" element={<MainApp />} />
      </Routes>
    </AppProvider>
  );
}
